import { useEffect, useRef, useState } from 'react';

const parse = (raw) => {
  const value = String(raw ?? '');
  const match = value.match(/^([^\d-]*)(-?[\d,.]+)(.*)$/);
  if (!match) return { prefix: '', target: NaN, suffix: '', decimals: 0, original: value };
  const prefix = match[1] || '';
  const numericStr = match[2];
  const suffix = match[3] || '';
  const dot = numericStr.indexOf('.');
  const decimals = dot >= 0 ? numericStr.length - dot - 1 : 0;
  const target = parseFloat(numericStr.replace(/,/g, ''));
  return { prefix, target, suffix, decimals, original: value };
};

const format = (n, decimals) => {
  if (Number.isNaN(n)) return '';
  return decimals > 0
    ? n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Math.round(n).toLocaleString('en-US');
};

export default function CountUp({
  value,
  duration = 1600,
  className,
  as: Tag = 'span',
  ...rest
}) {
  const ref = useRef(null);
  const { prefix, target, suffix, decimals, original } = parse(value);
  // Start on the final value so prerendered HTML (and crawlers) show the real
  // number and hydration matches it; drop to 0 only once we're animating.
  const [display, setDisplay] = useState(() =>
    Number.isFinite(target) ? `${prefix}${format(target, decimals)}${suffix}` : original
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || !Number.isFinite(target)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(`${prefix}${format(target, decimals)}${suffix}`);
      return;
    }

    setDisplay(`${prefix}${format(0, decimals)}${suffix}`);
    let rafId;
    let started = false;
    const startAnim = () => {
      if (started) return;
      started = true;
      const startTime = performance.now();
      const step = (now) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(`${prefix}${format(target * eased, decimals)}${suffix}`);
        if (t < 1) rafId = requestAnimationFrame(step);
      };
      rafId = requestAnimationFrame(step);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnim();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);

    return () => {
      cancelAnimationFrame(rafId);
      obs.disconnect();
    };
  }, [target, prefix, suffix, decimals, duration]);

  return (
    <Tag ref={ref} className={className} {...rest}>
      {display}
    </Tag>
  );
}
